/**
 * What may be exported, and what an export is allowed to contain.
 *
 * `includeSensitive` defaults to **false** and is the whole security posture of this surface (§37,
 * "exports omit sensitive fields by default"). Phone, email, address, guardian and staff notes
 * leave the system only when someone asks for them explicitly *and* holds the permission — a
 * spreadsheet mailed to a volunteer is the most common way a participant list escapes a community.
 */

import { z } from "zod";
import type { Permission } from "./permissions.js";
import { plainDateSchema, shortTextSchema, ulidSchema } from "./primitives.js";

/**
 * The report catalogue.
 *
 * Every one of these is generated the same way and comes back as a file — CSV or PDF — or as the
 * rows behind it, for the browser to build a workbook from or lay out and print (see `format`).
 *
 * `category_roster` and `category_tally` are the two sheets a community already keeps by hand — the
 * per-category sign-in sheet and the points tally across a weekend. Both take their columns from the
 * event rather than from a fixed list: a session becomes a column pair, a category dimension becomes
 * a column, and a registration form field becomes a column if it is asked for. That is what keeps
 * them one community's *layout* rather than one community's schedule baked into the code (D14, D22).
 *
 * Three of these are **blank sheets**, and they are not an afterthought. Registration happens at a
 * folding table outdoors, often with no signal, at an event that cannot be re-run (D22): the paper
 * is the fallback the whole system rests on, and paper that does not match the screen is worse than
 * no paper. So a blank sheet is generated from the same event configuration as the filled one — the
 * same questions, the same categories, the same sessions — rather than being a document somebody
 * keeps in a drawer and updates when they remember to.
 */
export const EXPORT_TYPES = [
  "registrations",
  "participants",
  "teams",
  "blank_registration_list",
  "category_roster",
  "category_tally",
  "check_in_list",
  "missing_documents",
  "judge_assignments",
  "judge_completion",
  "blank_score_sheet",
  "completed_score_sheet",
  "score_audit",
  "provisional_results",
  "final_results",
  "public_leaderboard",
  "prize_report",
  "blank_payout_sheet",
  "payout_signature_sheet",
  "payout_report",
  "event_schedule",
  "survey_results",
] as const;

export type ExportType = (typeof EXPORT_TYPES)[number];

/**
 * The sheets that are the day's work rather than a copy of the database.
 *
 * These are the ones somebody prints *at* a folding table: the blank sheet the desk falls back to,
 * the roster the gate ticks off, the signature sheet the treasurer collects on. They are reachable
 * with `export.print` as well as `export.create`, and never carry contact details whichever
 * permission opened them — which is what makes it safe to hand printing to a gate volunteer who
 * must not be handed the participant list with everybody's phone number on it (§37).
 *
 * Everything outside this set — the participant export, the survey answers, the payout ledger, the
 * score audit — is a copy of the database leaving the community, and stays behind `export.create`.
 *
 * The line is drawn by *what the sheet is for*, not by whether it happens to have a sensitive
 * column: `registrations` is on the list because a desk needs today's list of who is registered,
 * and its phone and email columns are simply never produced for a print grant.
 */
export const PRINTABLE_SHEETS = [
  "blank_registration_list",
  "registrations",
  "category_roster",
  "category_tally",
  "check_in_list",
  "blank_score_sheet",
  "blank_payout_sheet",
  "payout_signature_sheet",
  "event_schedule",
] as const satisfies readonly ExportType[];

export function isPrintableSheet(type: ExportType): boolean {
  return (PRINTABLE_SHEETS as readonly ExportType[]).includes(type);
}

/**
 * The read permission a report's *data* belongs to, where being allowed to export is not the
 * whole question.
 *
 * Until this existed, `export.create` was the only gate on any report, which quietly meant it was
 * a master key: an Analyst holds it and does not hold `payout.read`, so the Payouts screen is shut
 * to them — and the entire money ledger, every name and every amount, exported anyway. Adding
 * `export.print` for the desk made the same gap wider rather than causing it: a registrar would
 * have been able to print what every dancer was being paid, and a gate worker to print *draft*
 * standings, because the points tally reads result sets that have not been approved.
 *
 * So a report now says what it is made of, and the permission that governs that data is required
 * on top of the permission to export at all. Money is the case the system is most explicit about —
 * "deliberately separate from result approval" — and it was the one leaking.
 *
 * The blank payout sheet carries no amounts and is here anyway: it is the sheet the cash table is
 * run from, and who may stand at that table is the same question.
 *
 * Read by both sides. The server enforces it; the management app filters the sheets it offers by
 * the same map, so a person is never shown a sheet that will refuse them after they press Print.
 */
export const REPORT_DATA_PERMISSION = {
  payout_report: "payout.read",
  payout_signature_sheet: "payout.read",
  blank_payout_sheet: "payout.read",
  category_tally: "result.read",
} as const satisfies Partial<Record<ExportType, Permission>>;

/** What else this report needs, beyond permission to export. */
export function reportDataPermission(type: ExportType): Permission | null {
  return (REPORT_DATA_PERMISSION as Partial<Record<ExportType, Permission>>)[type] ?? null;
}

export const createExportSchema = z.object({
  type: z.enum(EXPORT_TYPES),
  /**
   * How the report comes back. Four shapes, one generation.
   *
   * - `csv` serialises here and downloads as a file.
   * - `pdf` is **rendered here too**, as a real document with fonts embedded (D97). It was the
   *   browser's print dialog until the platform left the free plan: a Worker with 10 ms of CPU
   *   could not typeset, and a PDF with no embedded font can only use the base-14 faces, which
   *   have no macron and no syllabics. The first is no longer true and the second is now answered
   *   rather than avoided — see `@powwow/pdf`.
   * - `xlsx` comes back as rows and headings for the browser to assemble into a workbook (D81).
   *   A zip of six XML parts is work the reader's machine can do, and the alternative is three
   *   hundred kilobytes of library in the bundle of an app used on rural connections.
   * - `print` is the same rows, for the browser to lay out under `@media print`. A desk that wants
   *   paper in the next ten seconds should not wait for a document to be built and downloaded
   *   first, and the sheet on the screen is the one it prints.
   *
   * The report is generated identically whichever is asked for, and writes one `export_jobs` row
   * carrying the format — so a PDF of the participant list is on the record exactly like a CSV of
   * it, and "who took a copy, and in what form" has an answer. That is the point of this whole
   * surface (§37).
   */
  format: z.enum(["csv", "xlsx", "pdf", "print"]).default("csv"),
  /** A subset of the report's columns, in the caller's order. Empty means every column. */
  fields: z.array(z.string().min(1).max(60)).max(80).default([]),
  filters: z
    .object({
      competitionId: ulidSchema.optional(),
      categoryId: ulidSchema.optional(),
      sessionId: ulidSchema.optional(),
      surveyId: ulidSchema.optional(),
      status: shortTextSchema.optional(),
      from: plainDateSchema.optional(),
      to: plainDateSchema.optional(),
    })
    .default({}),
  /** Contact details, dates of birth and staff notes. Refused without `registration.read`. */
  includeSensitive: z.boolean().default(false),
  /** Leading comment rows naming the report and when it was generated. */
  includeHeader: z.boolean().default(true),
});

export type CreateExportInput = z.infer<typeof createExportSchema>;
